/**
 * Recover from a stale entry bundle after a deploy by reloading the page.
 *
 * Every route is code-split, so the entry bundle hard-codes the hashed filename of
 * every chunk it has not loaded yet. A tab left open across a deploy still runs the
 * previous build's entry bundle; the first navigation into an unvisited route then
 * asks for a chunk the new image no longer ships, the request 404s, and the route's
 * `lazy()` rejects straight into the error boundary. Vite raises `vite:preloadError`
 * for exactly this failure. A reload is the correct recovery: index.html is served
 * `no-cache`, so the reload lands on the same URL with the current bundle.
 *
 * The reload is rate-limited through sessionStorage so a build that is genuinely
 * broken (the chunk is missing from the *current* deploy too) surfaces in the error
 * boundary instead of reloading forever.
 */

/** sessionStorage key holding the epoch-ms of the last automatic reload. */
export const STALE_CHUNK_RELOAD_KEY = 'pa:stale-chunk-reload-at';

/** A second preload failure inside this window is treated as a real fault, not staleness. */
const RELOAD_COOLDOWN_MS = 30_000;

/** The slice of `window` the listener touches, so tests can pass a plain EventTarget. */
type ReloadTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'> & {
  location: Pick<Location, 'reload'>;
};

/** The slice of `Storage` the listener touches. */
type ReloadStorage = Pick<Storage, 'getItem' | 'setItem'>;

interface StaleChunkReloadOptions {
  target?: ReloadTarget;
  storage?: ReloadStorage;
  now?: () => number;
}

/** Reads the last reload stamp; an unusable storage reads as "never reloaded". */
const readLastReload = (storage: ReloadStorage): number | null => {
  try {
    const raw = storage.getItem(STALE_CHUNK_RELOAD_KEY);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
};

/** Records a reload stamp; a locked-down storage is not worth failing the recovery over. */
const writeLastReload = (storage: ReloadStorage, at: number): void => {
  try {
    storage.setItem(STALE_CHUNK_RELOAD_KEY, String(at));
  } catch {
    // Private-mode Safari and similar: recovery still proceeds, just without the guard.
  }
};

/**
 * Attaches the `vite:preloadError` listener and returns a function that detaches it.
 *
 * Defaults to the real window, sessionStorage and clock; every dependency is injectable
 * so the behaviour is testable without patching jsdom's `location`.
 */
export function installStaleChunkReload(options: StaleChunkReloadOptions = {}): () => void {
  const target = options.target ?? window;
  const storage = options.storage ?? window.sessionStorage;
  const now = options.now ?? Date.now;

  const onPreloadError = (event: Event): void => {
    const current = now();
    const last = readLastReload(storage);
    if (last !== null && current - last < RELOAD_COOLDOWN_MS) {
      return; // Let Vite rethrow so the error boundary shows the real fault.
    }
    writeLastReload(storage, current);
    event.preventDefault();
    target.location.reload();
  };

  target.addEventListener('vite:preloadError', onPreloadError);
  return () => target.removeEventListener('vite:preloadError', onPreloadError);
}
